import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCampaigns } from '../stores/campaigns'
import { supabase } from '@unvld/db'
import { TierGate } from '../components/TierGate'
import { ArrowLeft, Send, Save, Users, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || 'https://vvuhbraojktjsesoiljt.supabase.co'

const TAG_OPTIONS = ['VIP', 'regular', 'newcomer'] as const

export function CampaignEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const {
    currentCampaign,
    loading,
    recipientCount,
    recipientCountLoading,
    fetchCampaign,
    createCampaign,
    updateCampaign,
    fetchRecipientCount,
    clearCurrent,
  } = useCampaigns()

  const isNew = !id
  const isReadOnly = currentCampaign?.status === 'sent' || currentCampaign?.status === 'sending'

  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [filterEnabled, setFilterEnabled] = useState(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])
  const [minVisits, setMinVisits] = useState<number | ''>('')
  const [saving, setSaving] = useState(false)
  const [sending, setSending] = useState(false)
  const [showSendConfirm, setShowSendConfirm] = useState(false)

  // Load existing campaign
  useEffect(() => {
    if (id) {
      fetchCampaign(id)
    } else {
      clearCurrent()
    }
    return () => clearCurrent()
  }, [id])

  // Populate form when campaign loads
  useEffect(() => {
    if (currentCampaign && id) {
      setSubject(currentCampaign.subject || '')
      setBodyHtml(currentCampaign.body_html || '')
      const filter = currentCampaign.segment_filter
      if (filter && (filter.tags?.length || filter.min_visits)) {
        setFilterEnabled(true)
        setSelectedTags(filter.tags || [])
        setMinVisits(filter.min_visits || '')
      }
    }
  }, [currentCampaign, id])

  // Fetch recipient count when filter changes
  const buildSegmentFilter = useCallback(() => {
    if (!filterEnabled) return undefined
    const filter: { tags?: string[]; min_visits?: number } = {}
    if (selectedTags.length > 0) filter.tags = selectedTags
    if (minVisits !== '' && minVisits > 0) filter.min_visits = minVisits
    return Object.keys(filter).length > 0 ? filter : undefined
  }, [filterEnabled, selectedTags, minVisits])

  useEffect(() => {
    fetchRecipientCount(buildSegmentFilter())
  }, [filterEnabled, selectedTags, minVisits])

  const toggleTag = (tag: string) => {
    setSelectedTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  const handleSaveDraft = async () => {
    if (!subject.trim()) {
      toast.error('Bitte einen Betreff eingeben')
      return
    }
    setSaving(true)
    try {
      const segmentFilter = buildSegmentFilter() || null
      if (isNew) {
        const campaign = await createCampaign({
          subject: subject.trim(),
          body_html: bodyHtml,
          segment_filter: segmentFilter,
        })
        toast.success('Kampagne erstellt')
        navigate(`/campaigns/${campaign.id}/edit`, { replace: true })
      } else {
        await updateCampaign(id!, {
          subject: subject.trim(),
          body_html: bodyHtml,
          segment_filter: segmentFilter,
        })
        toast.success('Kampagne gespeichert')
      }
    } catch {
      toast.error('Fehler beim Speichern')
    } finally {
      setSaving(false)
    }
  }

  const handleSendClick = () => {
    if (!subject.trim()) {
      toast.error('Bitte einen Betreff eingeben')
      return
    }
    if (!bodyHtml.trim()) {
      toast.error('Bitte einen Inhalt eingeben')
      return
    }
    if (recipientCount === 0) {
      toast.error('Keine Empfänger für diese Kampagne')
      return
    }
    setShowSendConfirm(true)
  }

  const handleSendConfirmed = async () => {
    setShowSendConfirm(false)
    setSending(true)
    try {
      // Save draft first if new
      let campaignId = id
      const segmentFilter = buildSegmentFilter() || null
      if (isNew) {
        const campaign = await createCampaign({
          subject: subject.trim(),
          body_html: bodyHtml,
          segment_filter: segmentFilter,
        })
        campaignId = campaign.id
      } else {
        await updateCampaign(id!, {
          subject: subject.trim(),
          body_html: bodyHtml,
          segment_filter: segmentFilter,
        })
      }

      // Call campaign-send edge function
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        toast.error('Nicht angemeldet')
        setSending(false)
        return
      }

      const res = await fetch(`${SUPABASE_URL}/functions/v1/campaign-send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ campaign_id: campaignId }),
      })

      const data = await res.json()
      if (!res.ok) {
        toast.error(data.error || 'Fehler beim Senden')
        setSending(false)
        return
      }

      toast.success(`Kampagne gesendet (${data.sent} Empfänger)`)
      // Refresh campaign state
      if (campaignId) {
        await fetchCampaign(campaignId)
      }
      navigate('/campaigns')
    } catch {
      toast.error('Fehler beim Senden')
    } finally {
      setSending(false)
    }
  }

  if (!isNew && loading && !currentCampaign) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  return (
    <TierGate feature="campaigns">
    <div className="max-w-3xl">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate('/campaigns')}
          className="p-2 text-slate-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isNew ? 'Neue Kampagne' : 'Kampagne bearbeiten'}
          </h1>
          {!isNew && currentCampaign && (
            <p className="text-sm text-slate-400 mt-0.5">
              Status: {currentCampaign.status === 'draft' ? 'Entwurf' : currentCampaign.status}
            </p>
          )}
        </div>
      </div>

      {/* Form */}
      <div className="space-y-6">
        {/* Subject */}
        <div className="bg-[#131316] border border-white/5 rounded-xl p-5">
          <label className="block text-sm font-medium text-slate-300 mb-2">Betreff</label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="z.B. Diesen Samstag: Exklusive Theme Night"
            disabled={isReadOnly}
            className="w-full px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
          />
        </div>

        {/* Body */}
        <div className="bg-[#131316] border border-white/5 rounded-xl p-5">
          <label className="block text-sm font-medium text-slate-300 mb-2">Inhalt (HTML)</label>
          <textarea
            value={bodyHtml}
            onChange={(e) => setBodyHtml(e.target.value)}
            placeholder="<h1>Hallo!</h1><p>Wir laden dich herzlich ein...</p>"
            rows={12}
            disabled={isReadOnly}
            className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 font-mono text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 transition-colors resize-y"
          />
          <p className="text-xs text-slate-500 mt-2">
            HTML-Format. Wird als E-Mail-Body versendet.
          </p>
        </div>

        {/* Segment Filter */}
        <div className="bg-[#131316] border border-white/5 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-medium text-slate-300">Empfänger</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Wer soll diese Kampagne erhalten?
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Users className="w-4 h-4 text-slate-400" />
              {recipientCountLoading ? (
                <Loader2 className="w-4 h-4 text-slate-400 animate-spin" />
              ) : (
                <span className="text-white font-medium">
                  {recipientCount ?? 0} Empfänger
                </span>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {/* Toggle */}
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!filterEnabled}
                onChange={() => {
                  setFilterEnabled(false)
                  setSelectedTags([])
                  setMinVisits('')
                }}
                disabled={isReadOnly}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
              />
              <span className="text-sm text-slate-300">An alle Newsletter-Abonnenten</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={filterEnabled}
                onChange={() => setFilterEnabled(true)}
                disabled={isReadOnly}
                className="w-4 h-4 rounded border-white/20 bg-white/5 text-indigo-500 focus:ring-indigo-500 focus:ring-offset-0"
              />
              <span className="text-sm text-slate-300">Gefilterte Zielgruppe</span>
            </label>

            {filterEnabled && (
              <div className="pl-7 space-y-4 pt-2 border-t border-white/5">
                {/* Tags */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2 uppercase">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {TAG_OPTIONS.map((tag) => (
                      <button
                        key={tag}
                        onClick={() => toggleTag(tag)}
                        disabled={isReadOnly}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          selectedTags.includes(tag)
                            ? 'bg-indigo-600 text-white'
                            : 'bg-white/5 text-slate-400 hover:bg-white/10 hover:text-white'
                        } disabled:opacity-50`}
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Min Visits */}
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-2 uppercase">
                    Mindestbesuche
                  </label>
                  <input
                    type="number"
                    min={0}
                    value={minVisits}
                    onChange={(e) =>
                      setMinVisits(e.target.value === '' ? '' : parseInt(e.target.value, 10))
                    }
                    placeholder="0"
                    disabled={isReadOnly}
                    className="w-32 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 text-sm focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:opacity-50 transition-colors"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        {!isReadOnly && (
          <div className="flex items-center gap-3 pt-2">
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              className="flex items-center gap-2 px-5 py-2.5 bg-white/10 hover:bg-white/15 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Als Entwurf speichern
            </button>
            {currentCampaign?.status === 'draft' || isNew ? (
              <button
                onClick={handleSendClick}
                disabled={saving || sending}
                className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {sending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                {sending ? 'Wird gesendet...' : 'Kampagne senden'}
              </button>
            ) : null}
          </div>
        )}

        {isReadOnly && (
          <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 text-sm text-slate-400">
            Diese Kampagne wurde bereits gesendet und kann nicht mehr bearbeitet werden.
            {currentCampaign && currentCampaign.open_count > 0 && (
              <div className="flex gap-6 mt-3 text-slate-300">
                <span>Geöffnet: {currentCampaign.open_count}</span>
                <span>Geklickt: {currentCampaign.click_count}</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>

    {/* Send Confirmation Modal */}
    {showSendConfirm && (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
        <div className="bg-[#131316] border border-white/10 rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
          <h2 className="text-lg font-bold text-white mb-2">Kampagne senden?</h2>
          <p className="text-sm text-slate-400 mb-6">
            Die Kampagne wird an <strong className="text-white">{recipientCount ?? 0} Empfänger</strong> versendet.
            Dieser Vorgang kann nicht rückgängig gemacht werden.
          </p>
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowSendConfirm(false)}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleSendConfirmed}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <Send className="w-4 h-4" />
              Jetzt senden
            </button>
          </div>
        </div>
      </div>
    )}
    </TierGate>
  )
}
